import { PrimitiveMeshRenderable } from "../primitives/PrimitiveMeshRenderable";
import { ArcRenderable } from "../primitives/ArcRenderable";
import { Mesh as ThreeMesh, Raycaster, CylinderGeometry, MeshBasicMaterial, DoubleSide } from "three";
import { Vector3 } from "flavr-math";
const dummyMesh = new ThreeMesh();
const raycaster = new Raycaster();
const tmpVector31 = new Vector3();
const tmpVector32 = new Vector3();
const tmpVector33 = new Vector3();
const tmpVector34 = new Vector3();
const tmpVector35 = new Vector3();
const AxisX = new Vector3(1, 0, 0);
const AxisY = new Vector3(0, 1, 0);
const CYLINDER_GEOMETRY = new CylinderGeometry(0.5, 0.5, 1, 12, 1);
const DUMMY_MATERIAL = new MeshBasicMaterial({
    color: 0xff0000,
    transparent: true,
    opacity: 0.5,
    side: DoubleSide
});
export class ConnectionRenderer {
    constructor(name, from, to) {
        this.name = name;
        this.from = from;
        this.to = to;
    }
    destroy() {
        if (this.from.connections) {
            const connections = this.from.connections.out.get(this.to.modelUID);
            if (connections) {
                connections.delete(this.name);
                if (connections.size === 0) {
                    this.from.connections.out.delete(this.to.modelUID);
                }
                this.from.connections.connectedModelsDirty = true;
            }
        }
        if (this.to.connections) {
            const connections = this.to.connections.in.get(this.from.modelUID);
            if (connections) {
                connections.delete(this.name);
                if (connections.size === 0) {
                    this.to.connections.in.delete(this.from.modelUID);
                }
                this.to.connections.connectedModelsDirty = true;
            }
        }
    }
    updateTransform(offsetAngle, startOffset, endOffset) {
        const startPoint = tmpVector31;
        const endPoint = tmpVector32;
        this.from.getCenter(startPoint);
        this.to.getCenter(endPoint);
        const dir = tmpVector33.subVectors(endPoint, startPoint).normalize();
        // For the offests we have to calculate two vectors U and V that are othogonal to the line direction and each other.
        // To calculate these vectors we use the cross product.
        // However for dir == AxisY or dir == -AxisY the result of the cross product is (0,0,0).
        // Therfore we use the X-Axis in the cross product if the line direction is close to the Y-Axis.
        const U = tmpVector34
            .copy(dir)
            .cross(Math.abs(dir.y) > 0.9 ? AxisX : AxisY)
            .normalize();
        const V = tmpVector35.copy(dir).cross(U).normalize();
        const cos = Math.cos(offsetAngle);
        const sin = Math.sin(offsetAngle);
        startPoint.addScaledVector(U, sin * startOffset);
        startPoint.addScaledVector(V, cos * startOffset);
        endPoint.addScaledVector(U, sin * endOffset);
        endPoint.addScaledVector(V, cos * endOffset);
        // recalculate the line direction after applying an offset to the start and end points
        dir.subVectors(endPoint, startPoint);
        let distance = dir.length();
        dir.normalize();
        if (distance === 0) {
            this.updateStartAndEndPoints(startPoint, endPoint);
            return;
        }
        let startDistance = 0;
        raycaster.set(startPoint, dir);
        raycaster.near = startDistance;
        raycaster.far = distance;
        startDistance = this.raycast(this.from, false);
        raycaster.near = startDistance;
        const hitDist = this.raycast(this.to, true);
        if (hitDist !== 0) {
            distance = hitDist;
        }
        endPoint.copy(startPoint).addScaledVector(dir, distance);
        startPoint.addScaledVector(dir, startDistance);
        this.updateStartAndEndPoints(startPoint, endPoint);
    }
    raycast(model, sortAscending) {
        const renderable = model.getRenderableByType(PrimitiveMeshRenderable);
        if (renderable) {
            if (renderable.instance) {
                return this.raycastGeometry(renderable.instance.geometry, model, sortAscending);
            }
        }
        else {
            const renderable = model.getRenderableByType(ArcRenderable);
            if (renderable) {
                return this.raycastGeometry(CYLINDER_GEOMETRY, model, sortAscending);
            }
        }
        return 0;
    }
    raycastGeometry(geometry, model, sortAscending) {
        dummyMesh.geometry = geometry;
        dummyMesh.material = DUMMY_MATERIAL;
        dummyMesh.matrixWorld.copy(model.matrixWorld);
        const intersects = [];
        dummyMesh.raycast(raycaster, intersects);
        if (sortAscending) {
            intersects.sort((a, b) => a.distance - b.distance);
        }
        else {
            intersects.sort((a, b) => b.distance - a.distance);
        }
        return intersects.length > 0 ? intersects[0].distance : 0;
    }
}
